import { Component, Prop, Element, Event, EventEmitter, Host, h } from '@stencil/core';
import { setCSSProps, createNamespaceKey } from '../../utils/utils';

/**
 * The Expand component is a standalone component used for progressive disclosure, organizing content under 
 * a relevant heading, which is used as a control for revealing and hiding its content.
 * 
 * @slot - Hidden/revealed content is placed in the default slot.
 * @slot cbp-expand-label - Optionally the component label/heading may be slotted via named slot if it contains markup rather than plain text.
 */
@Component({
  tag: 'cbp-expand',
  styleUrl: 'cbp-expand.scss'
})
export class CbpExpand {
  private control: HTMLElement;
  private button: HTMLButtonElement;

  @Element() host: HTMLElement;

  /**
   * Specifies an optional `id` for the component item heading, also used to generate an `id` for 
   * the content wrapper. If this property is not specified, a unique string will 
   * automatically be generated. 
   */
  @Prop() headingId: string = createNamespaceKey('cbp-expand');
  
  /** Specifies whether the content is expanded and visible. */
  @Prop({ reflect: true }) open: boolean = false;
  
  /** The component control label. */
  @Prop() label: string;

  /** The heading level of the accordion item control. Defaults to h3. */
  @Prop() headingLevel: 'h2' | 'h3' | 'h4' | 'h5' | 'h6' = 'h4';
  
  /** Specifies the context of the component as it applies to the visual design and whether it inverts when light/dark mode is toggled. Default behavior is "light-inverts" and does not have to be specified. */
  @Prop({ reflect: true }) context: "light-inverts" | "light-always" | "dark-inverts" | "dark-always";
  
  /** Supports adding inline styles as an object */
  @Prop() sx: any = {};


  /** A custom event emitted when the accordion item control is activated. */
  @Event() expandClick: EventEmitter;
  handleClick() {
    this.open = !this.open;
    this.expandClick.emit({
      host: this.host,
      button: this.button,
      open: this.open,
    });
    this.button.focus();
  }

  componentWillLoad() {
    if (typeof this.sx == 'string') {
      this.sx = JSON.parse(this.sx) || {};
    }
    setCSSProps(this.host, {
      ...this.sx,
    });
  }

  componentDidLoad() {
    this.button = this.control.querySelector('button');
  }

  render() {
    return (
      <Host>
        <cbp-flex
          class="cbp-expand--control"
          alignItems="flex-start"
          gap="var(--cbp-space-1x)"
          onClick={() => this.handleClick()}
        >
          <cbp-flex-item>
            <cbp-button
              class="cbp-expand--toggle"
              fill="ghost"
              color="secondary"
              width="var(--cbp-space-6x)"
              height="var(--cbp-space-6x)"
              context={this.context}
              controls={`${this.headingId}-content`}
              expanded={`${this.open}`}
              aria-labelledby={this.headingId}
              ref={el => (this.control = el)}
            >
              <cbp-icon name="caret-down" size="var(--cbp-space-3x)"></cbp-icon>
            </cbp-button>
          </cbp-flex-item>

          <cbp-flex-item id={this.headingId} flexGrow={1}>
            { this.host.querySelector('[slot="cbp-expand-label"]')
              ? <slot name="cbp-expand-label" />
              : <cbp-typography 
                  tag={this.headingLevel} 
                  variant="heading-xs"
                  context={this.context}
                  sx='{"line-height":"var(--cbp-space-6x)"}'
                >
                  {this.label}
                </cbp-typography>
            }
          </cbp-flex-item>
        </cbp-flex>

        <div id={`${this.headingId}-content`} class="cbp-expand--content">
          <slot />
        </div>
      </Host>

    );
  }

}
